/**
 * @file index.tsx
 *
 * @fileoverview The header will combine some of the navigation and user components
 * together to render them as one single header element.
 */

import React, { startTransition, useContext, useState } from 'react';
import GET_MENU from '~/jobiqo/components/Header/graphql/getMenuAuth.gql';
import GET_MENU_ANON from '~/jobiqo/components/Header/graphql/getAppInfoAnon.gql';
import Link from '~/jobiqo/components/Link';
import { AppContext, PathContext } from '~/jobiqo/context/app';
import { AppContextInterface, PathContextInterface } from '~/jobiqo/models/app';
import { Branding } from '~/jobiqo/components/Branding';
import DesktopMainNavigation from './components/Desktop/MainNavigation';
import { UserContextInterface, UserContext } from '~/jobiqo/context/user';
import { FormattedMessage, useIntl } from 'react-intl';
import dynamic from 'next/dynamic';
import { ErrorBoundary } from 'react-error-boundary';
import { isAnonymous, isApplicant, isRecruiter } from '~/jobiqo/lib/auth';
import { User } from '~/jobiqo/models/user';
import { QueryParams } from '~/jobiqo/models/graphql';
import { useQuery } from '@apollo/client';
import { Menu } from '~/jobiqo/models/navigation';
import MobileHeader from './components/Mobile/Header';
import HeaderEnvironmentIndicator from '~/jobiqo/components/Header/components/EnvironmentIndicator';
import { useIsMobile } from '~/vcl/hooks/useIsMobile';
import { FixedNavbar } from '~/vcl/molecules/03-layout/02-header-fixed';
import { Navbar } from '~/vcl/molecules/03-layout/01-header';
import { HideDesktop } from '~/vcl/atoms/06-layout/02-visibility/01-hide-desktop';
import { HideMobile } from '~/vcl/atoms/06-layout/02-visibility/02-hide-mobile';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { LinkButton } from '~/vcl/atoms/01-forms/01-button/02-link-button';
import { LockIcon } from '~/vcl/icons/Lock';
import HeaderCustomSlot from '~/jobiqo/components/Slots/HeaderCustomSlot';
import { getFrontendTheme } from '~/jobiqo/utils/getFrontend';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import HeaderMenuSlot from '~/jobiqo/components/Slots/HeaderMenuSlot';
import HeaderTopCustomSlot from '~/jobiqo/components/Slots/HeaderTopCustomSlot';
import NextLink from 'next/link';
import SiteCampaign from '~/jobiqo/components/Header/components/SiteCampaign';
import { useConfig } from '~/jobiqo/context/config';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

const MobileDrawerMenu = dynamic(import('./components/Mobile/DrawerMenu'), {
  ssr: false,
});

interface HeaderProps {
  fixed?: boolean;
}

/**
 * Get the information about what query to run dependind on authentication status.
 *
 * @param {boolean} authenticated
 * True if the user is authentiated, false otherwise.
 */
const getMenuQuery = (authenticated, user: User): QueryParams => {
  const query = authenticated ? GET_MENU : GET_MENU_ANON;
  const appContext: AppContextInterface = useContext(AppContext);
  // In case an admin logs in the frontend we should show the main menu.
  const isAdminUser =
    !isRecruiter(user) && !isApplicant(user) && !isAnonymous(user);
  let variables: any = {
    main_menu_id:
      !authenticated || isAdminUser
        ? 'main'
        : isRecruiter(user)
        ? 'recruiter-menu'
        : 'applicant-menu',
    language: appContext.locale,
  };
  if (authenticated) {
    variables = {
      ...variables,
      user_menu_id: 'account',
      language: appContext.locale,
    };
  }
  return {
    query,
    variables,
  };
};

export const Header = React.memo(({ fixed = false }: HeaderProps) => {
  const frontendConfig = useFrontendConfig();
  const appContext: AppContextInterface = useContext(AppContext);
  const pathContext: PathContextInterface = useContext(PathContext);
  const userContext: UserContextInterface = useContext(UserContext);
  const [navOpen, setNavOpen] = useState(false);
  const isMobile = useIsMobile();
  const intl = useIntl();
  const frontendTheme = getFrontendTheme(frontendConfig);
  const DesktopNavTag = fixed ? FixedNavbar : Navbar;
  const { getConfigByName } = useConfig();

  const SITE_CAMPAIGN_ENABLED: boolean = !!getConfigByName(
    'site_campaign_enabled',
  )?.value[0];

  // Load menu data.
  const { query: MenuQuery, variables: menuVariables } = getMenuQuery(
    userContext.authenticated,
    userContext.user as User,
  );
  const {
    data: dataMenu = { menu: { items: [] }, user_menu: { items: [] } },
  } = useQuery(MenuQuery, {
    variables: menuVariables,
  });

  const { menu, user_menu }: { menu: Menu; user_menu: Menu } = dataMenu;

  return (
    <>
      <ErrorBoundary fallback={null}>
        {isMobile && navOpen && (
          <MobileDrawerMenu
            navOpen={navOpen}
            onToggleNav={() => {
              startTransition(() => {
                setNavOpen(!navOpen);
              });
            }}
            menu={menu}
            userMenu={user_menu}
            {...{ appContext, pathContext, userContext }}
          />
        )}
      </ErrorBoundary>
      <HeaderEnvironmentIndicator />
      <HeaderTopCustomSlot />
      {SITE_CAMPAIGN_ENABLED && <SiteCampaign path={pathContext.path} />}
      <header>
        <HideDesktop>
          <MobileHeader
            NavTag={Navbar}
            searchLink={appContext.searchLink}
            toggleNav={() => {
              startTransition(() => {
                setNavOpen(!navOpen);
              });
            }}
          />
        </HideDesktop>
        <HideMobile>
          <DesktopNavTag>
            <Spacing margin={0} marginRight={6} padding={0}>
              <Branding
                {...(frontendTheme.NAVIGATION.LOGO
                  ? { customLogo: frontendTheme.NAVIGATION.LOGO }
                  : null)}
              />
            </Spacing>
            <FlexItems
              flexWrap="wrap"
              justifyContent={['center', 'center', 'flex-start']}
            >
              <Spacing>
                <ErrorBoundary fallback={null}>
                  <DesktopMainNavigation
                    menu={menu}
                    userMenu={user_menu}
                    path={pathContext.path}
                    querystring={pathContext.querystring}
                  />
                </ErrorBoundary>
              </Spacing>
              {!userContext.authenticated &&
                frontendTheme.NAVIGATION.SHOW_SIGN_IN_BUTTON && (
                  <div>
                    <Link asChild>
                      <LinkButton
                        linkElement={NextLink}
                        data-test-id="button_sign_in"
                        buttonStyle={
                          frontendTheme.NAVIGATION.SIGN_IN_BUTTON_STYLE
                        }
                        href={frontendTheme.NAVIGATION.SIGN_IN_URL}
                      >
                        <LockIcon
                          title={intl.formatMessage({
                            id: 'sign_in_icon',
                            defaultMessage: 'Sign in',
                          })}
                        />
                        <span>
                          <FormattedMessage
                            id="sign_in"
                            defaultMessage="Sign in"
                          />
                        </span>
                      </LinkButton>
                    </Link>
                  </div>
                )}
            </FlexItems>
            <HeaderMenuSlot userMenu={user_menu} />
          </DesktopNavTag>
        </HideMobile>
      </header>
      <HeaderCustomSlot />
    </>
  );
});
export default Header;
